import React, { useState, useEffect } from "react";
import pb from '../api/pocketbase';
import { exportToExcelCharges } from "./exportation/exportUtils";

export default function EtatImmoStructure({ onClose }) {
  const [filtreDateDebut, setFiltreDateDebut] = useState("");
  const [filtreDateFin, setFiltreDateFin] = useState("");
  const [immos, setImmos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");

  useEffect(() => {
    // On ne charge rien au montage, il faut filtrer pour voir les résultats
  }, []);

  const fetchImmos = async () => {
    setLoading(true);
    try {
      let all = await pb
        .collection("immoValideesDG")
        .getFullList({ sort: "-created" });
      // Filtrage par date
      if (filtreDateDebut || filtreDateFin) {
        all = all.filter((immo) => {
          let date =
            immo.dateValidationCG || immo.dateValidationDG || immo.created;
          if (!date) return false;
          date = new Date(date);
          const debut = filtreDateDebut ? new Date(filtreDateDebut) : null;
          const fin = filtreDateFin
            ? new Date(filtreDateFin + "T23:59:59")
            : null;
          return (!debut || date >= debut) && (!fin || date <= fin);
        });
      }
      setImmos(all);
    } catch (e) {
      setImmos([]);
    }
    setLoading(false);
  };

  const handleFiltrer = (e) => {
    e.preventDefault();
    if (!filtreDateDebut || !filtreDateFin) {
      alert("Veuillez saisir la date de début et la date de fin pour filtrer.");
      return;
    }
    fetchImmos();
  };

  const handleReset = () => {
    setFiltreDateDebut("");
    setFiltreDateFin("");
    setImmos([]);
  };

  // Regroupement par rubrique puis sous-rubrique
  const grouped = immos.reduce((acc, immo) => {
    let rubrique = "Autre";
    let sousRubrique = "Autre";
    if (immo.rubrique) {
      if (typeof immo.rubrique === "object") {
        rubrique = immo.rubrique.rubrique || "Autre";
        sousRubrique = immo.rubrique.sousRubrique || "Autre";
      } else {
        rubrique = immo.rubrique;
      }
    }
    if (!acc[rubrique]) acc[rubrique] = {};
    if (!acc[rubrique][sousRubrique]) acc[rubrique][sousRubrique] = [];
    acc[rubrique][sousRubrique].push(immo);
    return acc;
  }, {});

  const total = immos.reduce((sum, c) => sum + Number(c.montant || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 overflow-auto max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-blue-800">
            Historique des immobilisations de structure
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>
        <form
          onSubmit={handleFiltrer}
          className="flex gap-4 px-6 py-4 bg-gray-50 border-b"
        >
          <input
            type="date"
            value={filtreDateDebut}
            onChange={(e) => setFiltreDateDebut(e.target.value)}
            className="border rounded px-3 py-2"
            required
          />
          <input
            type="date"
            value={filtreDateFin}
            onChange={(e) => setFiltreDateFin(e.target.value)}
            className="border rounded px-3 py-2"
            required
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Filtrer
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="bg-gray-200 px-4 py-2 rounded"
          >
            Réinitialiser
          </button>
        </form>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : immos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucune immobilisation trouvée.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex items-center justify-between bg-blue-50 rounded-t-lg px-6 py-4 mb-0 border border-blue-100 border-b-0">
                <div className="flex items-center gap-4">
                  <span className="inline-block bg-blue-700 text-white font-bold px-4 py-1 rounded-full text-base tracking-wide">
                    IMMOBILISATIONS STRUCTURES
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-bold text-blue-900 text-lg">
                    Total : {total.toLocaleString()} FCFA
                  </span>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow"
                    onClick={() =>
                      exportToExcelCharges({
                        projet: "Immobilisations structures",
                        chefChantier: "-",
                        charges: immos,
                        total,
                        filtreDateDebut,
                        filtreDateFin,
                      })
                    }
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Extraire
                  </button>
                </div>
              </div>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-blue-50 border-y border-blue-100">
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                      Rubrique
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                      Montant
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grouped).map(([rubrique, sousRubriques]) => {
                    // Total rubrique
                    const totalRubrique = Object.values(sousRubriques)
                      .flat()
                      .reduce((sum, c) => sum + Number(c.montant || 0), 0);
                    return (
                      <React.Fragment key={rubrique}>
                        <tr>
                          <td
                            className="bg-blue-700 border-l-4 border-blue-900 px-6 py-3 font-bold text-white text-base"
                            colSpan={5}
                          >
                            {rubrique}
                            <span className="float-right">
                              {totalRubrique.toLocaleString()} FCFA
                            </span>
                          </td>
                        </tr>
                        {Object.entries(sousRubriques).map(
                          ([sousRubrique, immosSR]) => {
                            const totalSR = immosSR.reduce(
                              (sum, c) => sum + Number(c.montant || 0),
                              0
                            );
                            return (
                              <React.Fragment key={sousRubrique}>
                                <tr>
                                  <td
                                    className="bg-blue-100 border-l-4 border-blue-400 px-8 py-2 font-semibold text-blue-900"
                                    colSpan={5}
                                  >
                                    {sousRubrique}
                                    <span className="float-right">
                                      {totalSR.toLocaleString()} FCFA
                                    </span>
                                  </td>
                                </tr>
                                {immosSR.map((immo) => (
                                  <tr key={immo.id} className="hover:bg-blue-50 transition">
                                    <td></td>
                                    <td className="px-6 py-3 text-gray-600">
                                      {immo.description ? (
                                        <button
                                          className="text-blue-700 underline hover:text-blue-900"
                                          onClick={() => {
                                            setSelectedDescription(immo.description);
                                            setShowDescriptionModal(true);
                                          }}
                                          type="button"
                                        >
                                          Voir description
                                        </button>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-3 text-right font-semibold text-gray-900">
                                      {Number(immo.montant).toLocaleString()} FCFA
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      {immo.statut === "validé" && (
                                        <span className="text-green-700 font-bold">
                                          Validé
                                        </span>
                                      )}
                                      {immo.statut === "rejeté" && (
                                        <span className="text-red-700 font-bold">
                                          Rejeté
                                        </span>
                                      )}
                                      {immo.statut === "en attente" && (
                                        <span className="text-gray-500">
                                          En attente
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      {immo.dateValidationCG
                                        ? new Date(immo.dateValidationCG).toLocaleDateString("fr-FR")
                                        : immo.dateValidationDG
                                        ? new Date(immo.dateValidationDG).toLocaleDateString("fr-FR")
                                        : immo.created
                                        ? new Date(immo.created).toLocaleDateString("fr-FR")
                                        : "-"}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          }
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 border-t-2 border-blue-100">
                    <td
                      colSpan="2"
                      className="px-4 py-3 text-right font-bold text-gray-700"
                    >
                      Total
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-900">
                      {total.toLocaleString()} FCFA
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {/* Modale description */}
          {showDescriptionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Description détaillée
                  </h3>
                  <button
                    onClick={() => setShowDescriptionModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="text-xl">✗</span>
                  </button>
                </div>
                <div className="px-6 py-4">
                  <p className="text-gray-700 whitespace-pre-line">
                    {selectedDescription}
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setShowDescriptionModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
